'use client';

import { createContext, useContext, ReactNode, useEffect } from 'react';
import NotificationPermissionModal from '@/components/Modal/NotificationPermissionModal';
import { useNotificationPermission, ensureSubscription } from '@/hooks/useNotificationPermission';
import report, { ReportEvent } from '@/lib/report';
import useAuthStore from '@/store/useAuthStore';

interface NotificationPermissionContextType {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  shouldPromptNotification: () => boolean;
  getPermissionStatus: () => NotificationPermission | 'unsupported';
}

const NotificationPermissionContext = createContext<NotificationPermissionContextType | undefined>(undefined);

/**
 * 全局通知权限管理 Provider
 *
 * 提供全局的通知权限弹窗，可以在应用的任何地方触发显示
 *
 * @example
 * ```tsx
 * // 在 layout 或 app 中使用
 * <NotificationPermissionProvider>
 *   {children}
 * </NotificationPermissionProvider>
 *
 * // 在任何组件中使用
 * const { setShowModal, shouldPromptNotification } = useNotificationPermissionContext();
 *
 * if (shouldPromptNotification()) {
 *   setShowModal(true);
 * }
 * ```
 */
export function NotificationPermissionProvider({ children }: { children: ReactNode }) {
  const {
    showModal,
    setShowModal,
    handleEnableNotifications,
    shouldPromptNotification,
    getPermissionStatus
  } = useNotificationPermission();

  // Get user from auth store to check authentication status
  const user = useAuthStore(state => state.user);

  // 包装 handleEnableNotifications，立即关闭弹窗
  const handleEnableNotificationsAndClose = async () => {
    // 立即关闭弹窗
    setShowModal(false);
    // 然后在后台处理通知订阅
    await handleEnableNotifications();
  };

  // 获取当前权限状态
  const permissionStatus = getPermissionStatus();
  const permissionState = permissionStatus === 'unsupported' ? 'denied' : permissionStatus;

  // 在组件挂载时，检查用户是否已授权但未订阅，并上报权限状态
  // Only run when user is authenticated to avoid 401 errors
  useEffect(() => {
    // Skip if user is not authenticated
    if (!user) {
      console.log('[NotificationPermissionProvider] Skipping subscription check - user not authenticated');
      return;
    }

    const checkAndEnsureSubscription = async () => {
      // 上报用户通知权限状态
      report(ReportEvent.NOTIFICATION_PERMISSION_STATUS, {
        permissionState: permissionStatus,
        timestamp: Date.now(),
      });

      if (permissionStatus === 'granted') {
        console.log('[NotificationPermissionProvider] Permission granted, ensuring subscription...');
        const result = await ensureSubscription();

        // 上报订阅状态
        report(ReportEvent.NOTIFICATION_PERMISSION_STATUS, {
          permissionState: permissionStatus,
          hasSubscription: result.success,
          subscriptionExists: result.alreadyExists,
          timestamp: Date.now(),
        });

        if (result.success) {
          if (result.alreadyExists) {
            console.log('[NotificationPermissionProvider] ✅ Subscription already exists');
          } else {
            console.log('[NotificationPermissionProvider] ✅ Subscription created');
          }
        } else if (result.error) {
          console.warn('[NotificationPermissionProvider] ⚠️ Failed to ensure subscription:', result.error);
        }
      }
    };

    checkAndEnsureSubscription();
  }, [permissionStatus, user]);

  return (
    <NotificationPermissionContext.Provider
      value={{
        showModal,
        setShowModal,
        shouldPromptNotification,
        getPermissionStatus
      }}
    >
      {children}

      {/* 全局通知权限弹窗 */}
      <NotificationPermissionModal
        visible={showModal}
        onClose={() => setShowModal(false)}
        onEnable={handleEnableNotificationsAndClose}
        permissionState={permissionState}
      />
    </NotificationPermissionContext.Provider>
  );
}

/**
 * 使用通知权限 Context 的 Hook
 *
 * @throws 如果在 NotificationPermissionProvider 外部使用会抛出错误
 */
export function useNotificationPermissionContext() {
  const context = useContext(NotificationPermissionContext);
  if (context === undefined) {
    throw new Error('useNotificationPermissionContext must be used within NotificationPermissionProvider');
  }
  return context;
}

export default NotificationPermissionProvider;
